import React, { useContext, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { LocalizeContext, LocalizeContextProps } from 'react-localize-redux';
import MultiChart from './MultiChart';
import { RequestStatus } from '../../Request/types';
import type { SurgeryChartData } from '../types';

const STATUS_I18N_KEY: Partial<Record<RequestStatus, string>> = {
  [RequestStatus.PENDING_PAYMENT]: 'PENDING_PAYMENT',
  [RequestStatus.CANCELED]: 'CANCELED',
  [RequestStatus.COMPLETED]: 'COMPLETED',
  [RequestStatus.PAYMENT_COMPLETED]: 'PAYMENT_COMPLETED',
  [RequestStatus.READY_FOR_SURGERY]: 'READY_FOR_SURGERY',
  [RequestStatus.CHECKED_IN]: 'CHECKED_IN',
  [RequestStatus.PENDING_ANESTHESIOLOGIST]: 'PENDING_ANESTHESIOLOGIST',
  [RequestStatus.ANESTHESIOLOGIST_REJECTED]: 'ANESTHESIOLOGIST_REJECTED',
  [RequestStatus.PENDING_PURCHASE_SURGERY_PROTHESIS]: 'PENDING_PURCHASE_SURGERY_PROTHESIS',
  [RequestStatus.ANESTHESIOLOGIST_ACCEPTED]: 'ANESTHESIOLOGIST_ACCEPTED',
};

interface SurgeryPipelineStatusChartProps {
  data: SurgeryChartData;
  title: string;
}

const SurgeryPipelineStatusChart: React.FC<SurgeryPipelineStatusChartProps> = ({ data, title }) => {
  const { translate } = useContext(LocalizeContext as React.Context<LocalizeContextProps>);

  const categories = useMemo(() => {
    const rows = data.pipelineRows ?? [];
    return rows.map((row) => {
      const key = STATUS_I18N_KEY[row.status as RequestStatus];
      if (!key) {
        return String(row.status);
      }
      return translate(`pages.hospital.services.request.status.${key}`) as string;
    });
  }, [data.pipelineRows, translate]);

  const series = useMemo(
    () => [{ name: title, data: data.series[0]?.data ?? [] }],
    [data.series, title],
  );

  if (!series[0].data.length) {
    return (
      <Box>
        <Typography variant="h6">{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {translate('hospital.analytics.no_data') as string}
        </Typography>
      </Box>
    );
  }

  return (
    <MultiChart
      series={series}
      categories={categories}
      title={title}
      horizontal
      type="bar"
      total={data.count}
    />
  );
};

export default SurgeryPipelineStatusChart;
